import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

import {
  Heart,
  Share,
  MoreHorizontal,
  Edit,
  ArrowRight,
  MapPin,
  Star,
  School,
  Landmark,
  Train,
  ChevronRight,
  Bus,
  ChevronLeft,
  X,
  ArrowDown,
  Mail,
  ArrowUp,
  Calendar,
  User,
  Save,
  Check,
  Trash2
} from 'lucide-react';

const backendUrl = 'https://python-backend-68898252993.us-central1.run.app';


const useResponsiveParksPerPage = () => {
  const [count, setCount] = useState(1);

  useEffect(() => {
    const updateCount = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setCount(3); // desktop
      } else if (width >= 768) {
        setCount(2); // tablet
      } else {
        setCount(1); // mobile
      }
    };

    updateCount(); // run on mount
    window.addEventListener("resize", updateCount);
    return () => window.removeEventListener("resize", updateCount);
  }, []);

  return count;
};

const useResponsiveSchoolsPerPage = () => {
  const [count, setCount] = useState(1); // default for mobile

  useEffect(() => {
    const updateCount = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setCount(3); // lg: 3 cards
      } else if (width >= 768) {
        setCount(2); // md: 2 cards
      } else {
        setCount(1); // sm: 1 card
      }
    };

    updateCount(); // set on mount
    window.addEventListener("resize", updateCount);
    return () => window.removeEventListener("resize", updateCount);
  }, []);

  return count;
};

const ListingDetails = ({ role }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // State for editable stats
  const [stats, setStats] = useState({
    average_value: '',
    price_per_sqft: '',
    homes_for_sale: 0
  });
  
  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const [editableStats, setEditableStats] = useState({
    average_value: '',
    price_per_sqft: '',
    homes_for_sale: 0
  });

  // Image Gallery State
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showThumbnails, setShowThumbnails] = useState(false);

  // Favorite State
  const [isFavorite, setIsFavorite] = useState(false);

  // Schedule Tour Modal
  const [showTourModal, setShowTourModal] = useState(false);

  // Local state for school pagination
  const [schoolPage, setSchoolPage] = useState(1);
  const schoolsPerPage = useResponsiveSchoolsPerPage();

  // Local state for parks pagination
  const [parksPage, setParksPage] = useState(1);
  const parksPerPage = useResponsiveParksPerPage();

  // Filter state for transit
  const [transitFilter, setTransitFilter] = useState('All');

  // Fetch community details from the database API
  useEffect(() => {
    const fetchCommunityDetails = async () => {
      setIsLoading(true);
      try {
        // Fetch detailed community data
        const response = await axios.get(`${backendUrl}/api/communities/${id}`);
        setListing(response.data);
        
        // Set stats from the response
        setStats({
          average_value: response.data.averageValue,
          price_per_sqft: response.data.pricePerSqFt,
          homes_for_sale: response.data.homesForSale
        });
        
        setEditableStats({
          average_value: response.data.averageValue,
          price_per_sqft: response.data.pricePerSqFt,
          homes_for_sale: response.data.homesForSale
        });
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching community details:', error);
        setIsLoading(false);
      }
    };
    
    fetchCommunityDetails();
  }, [id, role]);

  // Handle saving edited stats
  const handleSaveStats = async () => {
    try {
      const token = localStorage.getItem('token'); // Assuming you store JWT in localStorage
      const response = await axios.put(
        `${backendUrl}/api/communities/${id}`, 
        {
          averageValue: editableStats.average_value,
          pricePerSqFt: editableStats.price_per_sqft,
          homesForSale: parseInt(editableStats.homes_for_sale)
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.status === 200) {
        // Update the listing and stats with the updated values
        setListing(prev => ({
          ...prev,
          averageValue: editableStats.average_value,
          pricePerSqFt: editableStats.price_per_sqft,
          homesForSale: parseInt(editableStats.homes_for_sale)
        }));
        
        setStats({
          average_value: editableStats.average_value,
          price_per_sqft: editableStats.price_per_sqft,
          homes_for_sale: parseInt(editableStats.homes_for_sale)
        });
        
        setIsEditing(false);
      } else {
        console.error('Failed to update community stats');
        alert('Failed to update stats. Please try again.');
      }
    } catch (error) {
      console.error('Error updating community stats:', error);
      alert('Error updating stats. Please try again.');
    }
  };

  const handleEdit = () => {
    // Navigate to the edit form with the community ID
    navigate(`/edit-community/${id}`, { state: { community: listing } });
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.delete(`${backendUrl}/api/communities/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        // Redirect to home page
        navigate('/');
      } else {
        alert('Failed to delete community. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting community:', error);
      alert('Error deleting community. Please try again.');
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };


  const renderDeleteModal = () => {
    if (!showDeleteModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
          <h2 className="text-xl font-bold mb-4 text-red-600">Delete Community</h2>
          <p className="mb-6">
            Are you sure you want to delete <strong>{listing.title}</strong>? This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-4">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              {loading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  };



  // Handle cancel editing
  const handleCancelEdit = () => {
    setEditableStats({
      average_value: stats.average_value,
      price_per_sqft: stats.price_per_sqft,
      homes_for_sale: stats.homes_for_sale
    });
    setIsEditing(false);
  };

  // Get images for the gallery
  const images = listing?.images || [];

  // Navigation for image slider
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Handle keydown events for gallery navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isFullScreen) return;
      
      if (e.key === 'ArrowRight') {
        nextImage();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'Escape') {
        setIsFullScreen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullScreen, images.length]);

  // ===== SCHOOLS PAGINATION =====
  let displayedSchools = [];
  let schoolTotalPages = 0;
  const { schools } = listing || {};

  if (schools && schools.length > 0) {
    schoolTotalPages = Math.ceil(schools.length / schoolsPerPage);
    const startIndex = (schoolPage - 1) * schoolsPerPage;
    const endIndex = startIndex + schoolsPerPage;
    displayedSchools = schools.slice(startIndex, endIndex);
  }

  // ===== PARKS PAGINATION =====
  let displayedParks = [];
  let parksTotalPages = 0;
  const { parks } = listing || {};

  if (parks && parks.length > 0) {
    parksTotalPages = Math.ceil(parks.length / parksPerPage);
    const startIndex = (parksPage - 1) * parksPerPage;
    const endIndex = startIndex + parksPerPage;
    displayedParks = parks.slice(startIndex, endIndex);
  }

  // ===== TRANSIT FILTERING =====
  const { transit } = listing || {};
  const filteredTransit = transit ? 
    (transitFilter === 'All' ? transit : transit.filter(t => t.mode === transitFilter)) 
    : [];
  
  // Get unique transit modes
  const transitModes = transit ? 
    ['All', ...new Set(transit.map(t => t.mode))] 
    : ['All'];

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-8 flex justify-center items-center h-96">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-32 h-32 bg-gray-200 rounded-full mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-32"></div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">Listing Not Found</h1>
        <p>We couldn't find a listing with ID {id}.</p>
        <Link to="/" className="text-blue-500 underline">
          Back to Communities
        </Link>
      </div>
    );
  }

  // Render the full-screen image gallery
  const renderFullScreenGallery = () => {
    if (!isFullScreen) return null;

    return (
      <div className="fixed inset-0 bg-black z-50 flex flex-col">
        {/* Header with controls */}
        <div className="flex justify-between items-center p-4 text-white">
          <div className="text-sm">
            {currentImageIndex + 1} / {images.length}
          </div>
          <div className="flex gap-4">
            <button 
              onClick={() => setShowThumbnails(!showThumbnails)}
              className="p-2 hover:bg-gray-800 rounded-full"
            >
              {showThumbnails ? <ArrowUp size={20} /> : <ArrowDown size={20} />}
            </button>
            <button 
              onClick={() => setIsFullScreen(false)}
              className="p-2 hover:bg-gray-800 rounded-full"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        {/* Main image */}
        <div className="flex-grow flex items-center justify-center relative">
          <button 
            onClick={prevImage}
            className="absolute left-4 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full"
          >
            <ChevronLeft size={24} />
          </button>
          
          <img 
            src={images[currentImageIndex]} 
            alt={`Property ${currentImageIndex + 1}`}
            className="max-h-full max-w-full object-contain"
          />
          
          <button 
            onClick={nextImage}
            className="absolute right-4 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full"
          >
            <ChevronRight size={24} />
          </button>
        </div>
        
        {/* Thumbnails */}
        {showThumbnails && (
          <div className="bg-black/80 p-4 ">
            <div className="flex gap-2 overflow-x-auto py-2">
              {images.map((img, idx) => (
                <div 
                  key={idx} 
                  onClick={() => goToImage(idx)}
                  className={`w-20 h-20 flex-shrink-0 cursor-pointer border-2 ${
                    idx === currentImageIndex ? 'border-blue-500' : 'border-transparent'
                  }`}
                >
                  <img 
                    src={img} 
                    alt={`Thumbnail ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render the schedule tour modal
  const renderTourModal = () => {
    if (!showTourModal) return null;

    return (
      <div className="fixed inset-0 bg-black/60 z-40 flex items-center justify-center">
        <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
          <button 
            onClick={() => setShowTourModal(false)}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
          
          <h2 className="text-xl font-bold mb-4">Schedule a Tour</h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <div className="flex border border-gray-300 rounded-lg p-2 items-center">
              <Calendar size={18} className="text-gray-400 mr-2" />
              <input 
                type="date" 
                className="flex-grow outline-none"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
            <div className="grid grid-cols-3 gap-2">
              <button className="border border-gray-300 rounded-lg p-2 hover:bg-gray-50">9:00 AM</button>
              <button className="border border-gray-300 rounded-lg p-2 hover:bg-gray-50">11:00 AM</button>
              <button className="border border-gray-300 rounded-lg p-2 hover:bg-gray-50">1:00 PM</button>
              <button className="border border-gray-300 rounded-lg p-2 hover:bg-gray-50">3:00 PM</button>
              <button className="border border-gray-300 rounded-lg p-2 hover:bg-gray-50">5:00 PM</button>
              <button className="border border-gray-300 rounded-lg p-2 hover:bg-gray-50">7:00 PM</button>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Info</label>
            <div className="flex border border-gray-300 rounded-lg p-2 items-center mb-2">
              <User size={18} className="text-gray-400 mr-2" />
              <input 
                type="text" 
                placeholder="Your Name"
                className="flex-grow outline-none"
              />
            </div>
            <div className="flex border border-gray-300 rounded-lg p-2 items-center">
              <Mail size={18} className="text-gray-400 mr-2" />
              <input 
                type="email" 
                placeholder="Your Email"
                className="flex-grow outline-none"
              />
            </div>
          </div>
          
          <button 
            className="w-full sm:w-auto ml-0 sm:ml-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 font-medium"
            onClick={() => setShowTourModal(false)}
          >
            Schedule Tour
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto bg-white">
      {/* Render the fullscreen gallery */}
      {renderFullScreenGallery()}
      
      {/* Render the tour modal */}
      {renderTourModal()}
      
      {renderDeleteModal()}

      {/* ======== GALLERY SECTION ======== */}
      <div className="relative w-full h-[500px] mb-8 overflow-hidden rounded-lg shadow-lg">
        {/* Main large image */}
        <div className="w-full h-full relative">
          <img
            src={images[currentImageIndex] || '/placeholder.jpg'}
            alt={listing.title}
            className="w-full h-full object-cover"
          />
          
          {/* Navigation controls */}
          <div className="absolute inset-0 flex items-center justify-between px-4">
            <button 
              onClick={prevImage}
              className="p-3 bg-white/80 hover:bg-white text-gray-800 rounded-full shadow transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft size={24} />
            </button>
            <button 
              onClick={nextImage}
              className="p-3 bg-white/80 hover:bg-white text-gray-800 rounded-full shadow transition-colors"
              aria-label="Next image"
            >
              <ChevronRight size={24} />
            </button>
          </div>
          
          {/* Thumbnails overlay */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 bg-black/50 rounded-full px-3 py-2">
            {images.slice(0, 5).map((_, idx) => (
              <button
                key={idx}
                onClick={() => goToImage(idx)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  idx === currentImageIndex ? 'bg-white' : 'bg-white/50 hover:bg-white/70'
                }`}
                aria-label={`Go to image ${idx + 1}`}
              />
            ))}
            {images.length > 5 && (
              <button className="text-white text-xs">+{images.length - 5}</button>
            )}
          </div>
          
          {/* Fullscreen button */}
          <button 
            onClick={() => setIsFullScreen(true)}
            className="absolute bottom-4 right-4 bg-black/70 text-white text-sm px-3 py-1 rounded-full hover:bg-black/80 transition-colors"
          >
            View All {images.length} Photos
          </button>
        </div>
      </div>

      {/* ======== CONTENT SECTION ======== */}
      <div className="px-8">
        {/* ======== HEADER + ACTIONS ======== */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="flex items-center text-gray-600 mb-2">
              <MapPin size={16} className="mr-1" />
              <p className="text-sm">Suburban Neighborhood in Henderson, Nevada • Clark County 89012</p>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">{listing.title}</h1>
            <div className="flex items-center">
              <div className="flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                <Star size={14} className="mr-1 fill-current" />
                <span>Premier Listing</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-start md:justify-end gap-2 mt-4 md:mt-0">




  <button 
    className={`p-2 rounded-full transition-colors duration-200 border ${
      isFavorite 
        ? 'bg-red-50 border-red-200' 
        : 'hover:bg-gray-100 border-gray-200'
    }`}
    onClick={() => setIsFavorite(!isFavorite)}
  >
    <Heart 
      size={18} 
      className={`${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-700'}`} 
    />
  </button>

  {/* Only show Edit and Delete buttons for owners */}
  {role === 'owner' && (
              <>
                <button 
                  onClick={handleEdit}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 border border-gray-200"
                  title="Edit Community"
                >
                  <Edit size={18} className="text-blue-600" />
                </button>
                
                <button 
                  onClick={() => setShowDeleteModal(true)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 border border-gray-200"
                  title="Delete Community"
                >
                  <Trash2 size={18} className="text-red-600" />
                </button>
              </>
            )}

  <button className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 border border-gray-200">
    <Share size={18} className="text-gray-700" />
  </button>
  <button 
    className="ml-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 font-medium"
    onClick={() => setShowTourModal(true)}
  >
    Schedule Tour
  </button>
</div>
        </div>

        {/* ======== KEY STATS ======== */}
        <div className="grid grid-cols-3 gap-6 bg-gray-50 p-4 md:p-6 rounded-xl mb-8 shadow-sm text-center relative">
          {isEditing && role === 'owner' && (
            <div className="absolute top-2 right-2 flex space-x-2">
              <button 
                onClick={handleSaveStats}
                className="p-1 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                title="Save changes"
              >
                <Save size={16} />
              </button>
              <button 
                onClick={handleCancelEdit}
                className="p-1 bg-gray-500 text-white rounded-full hover:bg-gray-600 transition-colors"
                title="Cancel editing"
              >
                <X size={16} />
              </button>
            </div>
          )}
          
          <div>
            <p className="text-xs md:text-sm text-gray-500 mb-1 leading-tight">
              Average <span className="block md:inline">Value</span>
            </p>
            {isEditing && role === 'owner' ? (
              <input 
                type="text"
                value={editableStats.average_value}
                onChange={(e) => setEditableStats(prev => ({...prev, average_value: e.target.value}))}
                className="text-lg md:text-2xl font-bold text-gray-800 border border-blue-300 rounded px-2 w-full"
              />
            ) : (
              <p className="text-lg md:text-2xl font-bold text-gray-800">
                {stats.average_value || listing.averageValue}
              </p>
            )}
          </div>
          
          <div className="border-x border-gray-200">
            <p className="text-xs md:text-sm text-gray-500 mb-1 leading-tight">
              Average <span className="block md:inline">$ / Sq Ft</span>
            </p>
            {isEditing && role === 'owner' ? (
              <input 
                type="text"
                value={editableStats.price_per_sqft}
                onChange={(e) => setEditableStats(prev => ({...prev, price_per_sqft: e.target.value}))}
                className="text-lg md:text-2xl font-bold text-gray-800 border border-blue-300 rounded px-2 w-full"
              />
            ) : (
              <p className="text-lg md:text-2xl font-bold text-gray-800">
               ${stats.price_per_sqft || listing.pricePerSqFt || '695'}
             </p>
           )}
         </div>
         
         <div>
           <p className="text-xs md:text-sm text-gray-500 mb-1 leading-tight">
             Homes <span className="block md:inline">For Sale</span>
           </p>
           {isEditing && role === 'owner' ? (
             <input 
               type="number"
               value={editableStats.homes_for_sale}
               onChange={(e) => setEditableStats(prev => ({...prev, homes_for_sale: parseInt(e.target.value) || 0}))}
               className="text-lg md:text-2xl font-bold text-gray-800 border border-blue-300 rounded px-2 w-full"
             />
           ) : (
             <p className="text-lg md:text-2xl font-bold text-gray-800">
               {stats.homes_for_sale || listing.homesForSale || '71'}
             </p>
           )}
         </div>
       </div>

       {/* Optional subheading */}
       {listing.subheading && (
         <h2 className="text-xl font-bold mb-4 text-gray-800">{listing.subheading}</h2>
       )}

       {/* ======== DESCRIPTION ======== */}
       <div className="text-gray-700 mb-8 space-y-4 leading-relaxed text-justify">
         <p>
           {listing.description}
         </p>
       </div>
       
       {/* ======== SCHOOLS SECTION ======== */}
       <div className="mb-10">
         <div className="flex items-center justify-between mb-4">
           <div className="flex items-center">
             <School size={22} className="text-blue-600 mr-2" />
             <h2 className="text-xl font-bold text-gray-800">Schools</h2>
           </div>
           <button className="text-blue-600 hover:text-blue-800 transition-colors duration-200 flex items-center text-sm font-medium">
             Explore All Schools
             <ChevronRight size={16} className="ml-1" />
           </button>
         </div>
         <p className="text-sm text-gray-500 mb-6">Source: Great Schools</p>

         {(!schools || schools.length === 0) ? (
           <div className="bg-gray-50 rounded-lg p-8 text-center">
             <p className="text-gray-600">No schools nearby</p>
           </div>
         ) : (
           <>
             {/* Display only the schools for the current page */}
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
               {displayedSchools.map((school, index) => (
                 <div
                   key={index}
                   className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200 transform hover:-translate-y-1 transition-transform"
                 >
                   <div className="relative">
                     <img
                       src={school.image}
                       alt={school.name}
                       className="w-full h-40 object-cover"
                     />
                     <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                       {(index + 1) + (schoolPage - 1) * schoolsPerPage} of {schools.length}
                     </div>
                   </div>
                   <div className="p-4">
                     <h3 className="font-semibold text-lg text-gray-800">{school.name}</h3>
                     <p className="text-sm text-gray-600 mb-2">
                       {school.grades} • {school.type}
                     </p>
                     {/* rating badge */}
                     <div className="flex items-center">
                       <span className="inline-block text-sm font-medium px-3 py-1 rounded-full bg-blue-100 text-blue-800">
                         Rating: {school.rating}
                       </span>
                     </div>
                   </div>
                 </div>
               ))}
             </div>

             {/* Pagination if schoolTotalPages > 1 */}
             {schoolTotalPages > 1 && (
               <div className="flex justify-center mt-6 space-x-2">
                 {schoolPage > 1 && (
                   <button
                     onClick={() => setSchoolPage(schoolPage - 1)}
                     className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                   >
                     <ChevronLeft size={16} />
                   </button>
                 )}
                 
                 {[...Array(schoolTotalPages)].map((_, i) => {
                   const pageNum = i + 1;
                   return (
                     <button
                       key={pageNum}
                       onClick={() => setSchoolPage(pageNum)}
                       className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors duration-200 ${
                         pageNum === schoolPage
                           ? 'bg-blue-600 text-white'
                           : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                       }`}
                     >
                       {pageNum}
                     </button>
                   );
                 })}
                 
                 {schoolPage < schoolTotalPages && (
                   <button
                     onClick={() => setSchoolPage(schoolPage + 1)}
                     className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                   >
                     <ChevronRight size={16} />
                   </button>
                 )}
               </div>
             )}
           </>
         )}
       </div>

       <hr className="my-8 border-gray-200" />

       {/* ======== PARKS SECTION ======== */}
       <div className="mb-10">
         <div className="flex items-center justify-between mb-4">
           <div className="flex items-center">
             <Landmark size={22} className="text-green-600 mr-2" />
             <h2 className="text-xl font-bold text-gray-800">Parks in this Area</h2>
           </div>
           <button className="text-blue-600 hover:text-blue-800 transition-colors duration-200 flex items-center text-sm font-medium">
             Explore All Parks
             <ChevronRight size={16} className="ml-1" />
           </button>
         </div>
         <p className="text-sm text-gray-500 mb-6">Source: Local Parks and Recreation</p>

         {(!parks || parks.length === 0) ? (
           <div className="bg-gray-50 rounded-lg p-8 text-center">
             <p className="text-gray-600">No parks nearby</p>
           </div>
         ) : (
           <>
             {/* Display only the parks for the current page */}
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
               {displayedParks.map((park, index) => (
                 <div
                   key={index}
                   className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 group"
                 >
                   <div className="relative overflow-hidden">
                     <img
                       src={park.image}
                       alt={park.name}
                       className="w-full h-40 object-cover transition-transform duration-500 group-hover:scale-110"
                     />
                     <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                       <div className="p-4 text-white">
                         <h3 className="font-semibold text-lg">{park.name}</h3>
                       </div>
                     </div>
                   </div>
                   <div className="p-4">
                     <h3 className="font-semibold text-lg text-gray-800 group-hover:text-green-600 transition-colors">{park.name}</h3>
                     <p className="text-sm text-gray-600">
                       {park.amenities}
                     </p>
                   </div>
                 </div>
               ))}
             </div>

             {/* Pagination if parksTotalPages > 1 */}
             {parksTotalPages > 1 && (
               <div className="flex justify-center mt-6">
                 <div className="inline-flex rounded-md shadow-sm">
                   <button
                     onClick={() => setParksPage(Math.max(1, parksPage - 1))}
                     disabled={parksPage === 1}
                     className={`px-3 py-2 text-sm font-medium rounded-l-md border border-gray-300 ${
                       parksPage === 1 
                         ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                         : 'bg-white text-gray-700 hover:bg-gray-50'
                     }`}
                   >
                     <ChevronLeft size={16} />
                   </button>
                   
                   {[...Array(parksTotalPages)].map((_, i) => {
                     const pageNum = i + 1;
                     return (
                       <button
                         key={pageNum}
                         onClick={() => setParksPage(pageNum)}
                         className={`px-3 py-2 text-sm font-medium border-t border-b border-gray-300 ${
                           pageNum === parksPage
                             ? 'bg-green-600 text-white border-green-600 z-10'
                             : 'bg-white text-gray-700 hover:bg-gray-50'
                         } ${i === parksTotalPages - 1 ? '' : 'border-r'}`}
                       >
                         {pageNum}
                       </button>
                     );
                   })}
                   
                   <button
                     onClick={() => setParksPage(Math.min(parksTotalPages, parksPage + 1))}
                     disabled={parksPage === parksTotalPages}
                     className={`px-3 py-2 text-sm font-medium rounded-r-md border border-gray-300 ${
                       parksPage === parksTotalPages 
                         ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                         : 'bg-white text-gray-700 hover:bg-gray-50'
                     }`}
                   >
                     <ChevronRight size={16} />
                   </button>
                 </div>
               </div>
             )}
           </>
         )}
       </div>

       <hr className="my-8 border-gray-200" />

       {/* ======== TRANSIT SECTION ======== */}
       <div className="mb-10">
         <div className="flex items-center justify-between mb-4">
           <div className="flex items-center">
             <Bus size={22} className="text-purple-600 mr-2" />
             <h2 className="text-xl font-bold text-gray-800">Transit</h2>
           </div>
         </div>

         {/* Transit filter tabs */}
         {transit && transit.length > 0 && (
           <div className="flex flex-wrap gap-2 mb-4">
             {transitModes.map(mode => (
               <button
                 key={mode}
                 onClick={() => setTransitFilter(mode)}
                 className={`px-4 py-1 text-sm rounded-full transition-colors ${
                   transitFilter === mode
                     ? 'bg-purple-600 text-white'
                     : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                 }`}
               >
                 {mode}
               </button>
             ))}
           </div>
         )}

         {!transit || transit.length === 0 ? (
           <div className="bg-gray-50 rounded-lg p-8 text-center">
             <p className="text-gray-600">No transit options nearby</p>
           </div>
         ) : filteredTransit.length === 0 ? (
           <div className="bg-gray-50 rounded-lg p-8 text-center">
             <p className="text-gray-600">No {transitFilter} transit options found</p>
           </div>
         ) : (
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
             {filteredTransit.map((t, index) => (
               <div
                 key={index}
                 className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 bg-white hover:border-purple-200 group"
               >
                 <div className="flex items-center mb-2">
                   {t.mode === 'Bus' && <Bus size={18} className="text-purple-600 mr-2" />}
                   {t.mode === 'Train' && <Train size={18} className="text-purple-600 mr-2" />}
                   {t.mode === 'Airport' && (
                     <svg xmlns="http://www.w3.org/2000/svg" className="h-[18px] w-[18px] text-purple-600 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                       <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                     </svg>
                   )}
                   <h3 className="font-semibold text-gray-800">{t.mode}</h3>
                 </div>
                 <p className="text-sm text-gray-700 font-medium">{t.name}</p>
                 {t.description && (
                   <p className="text-sm text-gray-600 mt-1">
                     {t.description}
                   </p>
                 )}
               </div>
             ))}
           </div>
         )}
       </div>

       {/* Back to Communities link */}
       <div className="mb-10 mt-12 flex justify-center">
         <Link
           to="/"
           className="px-6 py-3 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 transition-colors duration-200 flex items-center font-medium"
         >
           <ChevronLeft size={16} className="mr-2" />
           Back to Communities
         </Link>
       </div>
     </div>
   </div>
 );
};

export default ListingDetails;